import { RefObject, useEffect, useRef } from 'react';
import { Editor } from 'tinymce';
import { useTranslation } from 'react-i18next';
import debounce from '../../../../../lib/performance/debounce';

/**
 * This hook saves the current html of the tiny instance in a debounced manner.
 * this is needed as long as we use a formmodel where we need to save the payload on every change.
 *
 * a better approach for the future would be to only getContent({ format: 'html' }) as we save the form.
 */
export function useEditorContentToModel(
  editorInstance: Editor | null,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  maxLength: number | undefined,
  saveToForModel: (content: string) => void,
) {
  const { t } = useTranslation('Editor');

  const setCustomValidity = (error: string, reportImmediately = false) => {
    textareaRef.current?.setCustomValidity(error);

    if (reportImmediately) {
      textareaRef.current?.reportValidity();
    }
  };

  const checkEmptyContent = (content: string) => {
    const result = content.trim();
    if (result.length === 0) {
      setCustomValidity(t('errorEmptyContent'));
    }
  };

  const checkValidity = (content: string) => {
    textareaRef.current?.setCustomValidity('');

    // Prevent exceeding limit
    if (maxLength && content.length > maxLength) {
      setCustomValidity(t('errorTooManyCharacters', { maxlength: maxLength }), true);
    }

    checkEmptyContent(content);
  };
  const checkEmptyContentValidity = (content: string) => {
    textareaRef.current?.setCustomValidity('');

    checkEmptyContent(content);
  };

  // debounce method in ref so it can persist trough renders.
  // stackoverflow.com/questions/54666401/how-to-use-throttle-or-debounce-with-react-hook
  // getContent is expensive, but we have to do it on every change.
  const debouncedOnEvent = useRef(
    debounce((instance: Editor, checkFn: (content: string) => void, saveTo: (content: string) => void) => {
      const textContent = instance?.getContent({ format: 'text' });
      const htmlContent = instance?.getContent({ format: 'html' });
      // We need the text content to correctly measure the content length for our validation,
      // otherwise the whole html markup is counted...
      checkFn(textContent);
      // ... and we need the html content so we correctly save copy-pasted html content (like links)
      saveTo(htmlContent);
    }, 150),
  );

  // initially set empty content custom validity if there is no content yet.
  // otherwise users could add attachments and submit the form without adding text.
  useEffect(() => {
    if (!textareaRef.current?.value.length) {
      setCustomValidity(t('errorEmptyContent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editorInstance) {
      // on key up we do not check for max length anymore as it prevented the user from typing and therefore from
      // correcting, rewriting the text in a manor that it would fit the constraint. (see SIBZIEL-15200)
      const handleChangeKeyUp = () => {
        debouncedOnEvent.current(editorInstance, checkEmptyContentValidity, saveToForModel);
      };

      const handleBlur = () => {
        checkValidity(editorInstance?.getContent({ format: 'html' }));
        saveToForModel(editorInstance?.getContent({ format: 'html' }));
      };

      editorInstance.on('change', handleChangeKeyUp);
      editorInstance.on('keyup', handleChangeKeyUp);
      editorInstance.on('blur', handleBlur);

      // Cleanup event listeners on unmount
      return () => {
        editorInstance.off('change', handleChangeKeyUp);
        editorInstance.off('keyup', handleChangeKeyUp);
        editorInstance.off('blur', handleBlur);
      };
    }
  }, [editorInstance, saveToForModel]);
}
